/**
 * scheduleLegacyRedirectGuard — sends the entries `/schedule` cannot serve to
 * the previous-generation page at `/schedule-legacy`.
 *
 * RESPONSIBILITY: `/schedule` is the redesign now. Two kinds of entry still
 * belong on the older page, and this guard is the only thing that knows it:
 *
 *   • A LEGACY-SHAPED URL. `?type=desks|parking|zones`, `?tab=events`, the
 *     find-space panel, the saved cascade filters and the booking-panel
 *     restore params are all state only `ScheduleComponent` can read. The
 *     redesign ignores unknown params silently, so without this the link
 *     would render the room grid and quietly drop what the user asked for.
 *
 *   • A TENANT WITHOUT `rooms.booking`. The redesign is room-centric: its
 *     grid is rooms down against hours across, and it has no desks, parking
 *     or zones surface. A desks-only customer sent there gets a grid of rooms
 *     they cannot book, while the legacy page sanitises itself to their first
 *     visible sub-view.
 *
 * ⚠ THE PREVIOUS VERSION OF THIS GUARD RAN THE OTHER WAY, and the inversion
 * is the interesting part. It used to sit on `/schedule` — then the legacy
 * path — and forward bare deep links FORWARD to `/schedule-preview`, which
 * meant it had to ignore in-app navigations (`router.navigated`) so the legacy
 * page's own tab switches were not hijacked, and had to treat ANY query param
 * as legacy state because it could not tell the two vocabularies apart.
 *
 * Both compromises are gone. The legacy page's tab switches write onto
 * `/schedule-legacy` now, so nothing in-app lands here carrying legacy state,
 * and the answer is a pure function of the URL and the capability — the same
 * for a bookmark, a hard reload and a nav click. That also fixes a split this
 * app shipped with: the nav tile went straight to the redesign with no
 * capability check at all, so a desks-only customer reached the room grid by
 * clicking Schedule and the legacy page by pasting the URL.
 *
 * `?date=` is deliberately NOT a legacy signal. It is the one param both
 * pages read, and the redesign honours it, so `/schedule?date=2026-09-14`
 * opens the redesign on that day rather than bouncing.
 *
 * ⚠ NOT SUFFICIENT ON ITS OWN. A `canActivate` guard does not re-run when
 * only the query params change, and on a hash-routed app pasting
 * `#/schedule?type=desks` while already on the schedule is exactly that. The
 * `/schedule` route pairs this guard with a `runGuardsAndResolvers` predicate
 * built from `LEGACY_SCHEDULE_PARAMS` so that entry re-runs it; see the
 * comment there. Changing the list below changes both.
 *
 * Runs AFTER `authGuard` (unauthenticated hits bounce to login first; the
 * post-login return is a fresh boot and re-runs this guard) and BEFORE the
 * ANY-OF capability guard — a viewer holding none of the booking capabilities
 * is bounced home by whichever route they end up on, since both carry it.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ViewerService } from '../services/viewer.service';
import { FeatureFlagsViewerService } from '../services/feature-flags-viewer.service';

/**
 * Query params only `ScheduleComponent` reads. Derived from its own URL
 * restore code, not guessed: the resource type and tab, the find-space panel,
 * the five cascade filter keys it persists, the booking-panel restore set and
 * the people band.
 *
 * `date` is absent on purpose — see the header.
 */
export const LEGACY_SCHEDULE_PARAMS: readonly string[] = [
  'type',
  'tab',
  'panel',
  'location',
  'floor',
  'capacity',
  'available',
  'amenities',
  'bookRoom',
  'bookRoomName',
  'bookStart',
  'bookEnd',
  'people',
];

export const scheduleLegacyRedirectGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const viewerService = inject(ViewerService);
  const featureFlags = inject(FeatureFlagsViewerService);

  // Carried across, always. A legacy URL forwarded without its params lands
  // on the right page with the state stripped, which is the failure this
  // redirect exists to prevent.
  const toLegacy = () =>
    router.createUrlTree(['/schedule-legacy'], {
      queryParams: route.queryParams,
    });

  if (LEGACY_SCHEDULE_PARAMS.some((param) => param in route.queryParams)) {
    return toLegacy();
  }

  await viewerService.ensureLoaded();
  if (!featureFlags.is('rooms.booking')) return toLegacy();

  return true;
};
